import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = window.location.origin + "/api";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));

  // Set axios default header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      localStorage.setItem("auth_token", token);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      localStorage.removeItem("auth_token");
    }
  }, [token]);

  // Verify token on mount
  const verifyToken = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/verify`);
      if (response.data.valid) {
        setUser(response.data.user);
      } else {
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Login function
  const login = async (accessCode) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        access_code: accessCode
      });
      
      if (response.data.token) {
        setToken(response.data.token);
        setUser(response.data.user);
        return { success: true };
      }
      return { success: false, error: "Invalid response from server" };
    } catch (error) {
      console.error("Login error details:", {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        url: `${API}/auth/login`
      });
      
      // Provide specific error messages based on error type
      if (!error.response) {
        // Network error - no response received
        return { success: false, error: `서버 연결 실패 (${error.message}). 잠시 후 다시 시도해주세요.` };
      }
      
      const status = error.response.status;
      const detail = error.response.data?.detail;
      
      if (status === 401 || status === 403) {
        return { success: false, error: detail || "유효하지 않은 액세스 코드입니다" };
      } else if (status === 520 || status === 502 || status === 503) {
        return { success: false, error: `서버가 일시적으로 응답하지 않습니다 (${status}). 잠시 후 다시 시도해주세요.` };
      } else if (status >= 500) {
        return { success: false, error: `서버 오류 (${status}): ${detail || "내부 오류"}` };
      }
      
      return { success: false, error: detail || `로그인 실패 (${status})` };
    }
  };

  // Logout function
  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("auth_token");
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
